import { useEffect, useState } from "react";
import { Mail, Phone, Library, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { getConfig, sendContact } from "../lib/api";
import { Reveal } from "./Reveal";

export const Contact = () => {
    const [contact, setContact] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        getConfig().then((c) => setContact(c.contact)).catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (e) => setForm((f) => ({ ...f, message: e.detail.message }));
        window.addEventListener("cw:prefill-contact", handler);
        return () => window.removeEventListener("cw:prefill-contact", handler);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await sendContact(form);
            toast.success("Message sent — I'll get back to you shortly.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error("Could not send your message. Please email me directly.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-ink text-paper py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-10 grid md:grid-cols-2 gap-14 md:gap-24">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-paper/50">
                        <span className="h-px w-10 bg-worx inline-block" /> Contact
                    </p>
                    <h2 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-6xl">
                        Not sure where to start? Just ask.
                    </h2>
                    <p className="mt-6 text-paper/60 leading-relaxed max-w-md">
                        Tell me what you're trying to get done and I'll tell you honestly whether one session
                        can get you there.
                    </p>
                    <ul className="mt-10 space-y-5 text-paper/80">
                        <li className="flex items-center gap-4">
                            <Mail size={18} className="text-worx shrink-0" />
                            <a data-testid="contact-email" href={`mailto:${contact?.email || ""}`} className="hover:text-worx transition-colors">
                                {contact?.email || "hello@consultingworx.com"}
                            </a>
                        </li>
                        <li className="flex items-center gap-4">
                            <Phone size={18} className="text-worx shrink-0" />
                            <a data-testid="contact-phone" href={`tel:${contact?.phone || ""}`} className="hover:text-worx transition-colors">
                                {contact?.phone || "(555) 012-3456"}
                            </a>
                        </li>
                        <li className="flex items-center gap-4">
                            <Library size={18} className="text-worx shrink-0" />
                            <span data-testid="contact-city">Virtual on Google Meet — or in person at your place / the Palm Beach County Main Library, 3650 Summit Blvd, West Palm Beach, FL 33406</span>
                        </li>
                    </ul>
                </Reveal>
                <Reveal delay={0.1}>
                    <form onSubmit={submit} data-testid="contact-form" className="border border-paper/15 p-8 md:p-10">
                        <div className="space-y-5">
                            <div>
                                <Label htmlFor="ct-name" className="text-paper/70">Name</Label>
                                <Input id="ct-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-none bg-transparent border-paper/20 text-paper focus-visible:ring-worx" />
                            </div>
                            <div>
                                <Label htmlFor="ct-email" className="text-paper/70">Email</Label>
                                <Input id="ct-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-none bg-transparent border-paper/20 text-paper focus-visible:ring-worx" />
                            </div>
                            <div>
                                <Label htmlFor="ct-message" className="text-paper/70">Message</Label>
                                <Textarea id="ct-message" data-testid="contact-message-input" value={form.message} onChange={set("message")} required rows={5} className="mt-1.5 rounded-none bg-transparent border-paper/20 text-paper focus-visible:ring-worx" placeholder="What would you like to learn or build?" />
                            </div>
                            <Button data-testid="contact-submit-btn" type="submit" disabled={sending} className="w-full bg-worx hover:bg-paper hover:text-ink text-white rounded-none py-6 text-base transition-colors duration-200">
                                {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
                                Send message
                            </Button>
                        </div>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
